/**
 * Clipboard Utilities
 * Generic clipboard operations for copying text to clipboard with fallback support
 */

const { FRIEND_PAY_MESSAGES } = require('../constant/friendPayConstants');
const { showSuccessToast, showErrorToast } = require('./toast');

/**
 * Copy text to clipboard with success/error callbacks
 * Uses modern Clipboard API with fallback to execCommand for older browsers
 *
 * @param {string} text - Text to copy to clipboard
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback executed on successful copy
 * @param {Function} options.onError - Callback executed on copy failure
 * @returns {Promise<void>}
 */
exports.copyToClipboard = function(text, options = {}) {
  const {
    onSuccess = () => showSuccessToast(FRIEND_PAY_MESSAGES.LINK_COPIED),
    onError = () => showErrorToast(FRIEND_PAY_MESSAGES.COPY_FAILED + text)
  } = options;

  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        // If modern API fails, try fallback
        fallbackCopyToClipboard(text, { onSuccess, onError });
      });
  } else {
    // Use fallback for older browsers
    return fallbackCopyToClipboard(text, { onSuccess, onError });
  }
};

/**
 * Fallback clipboard copy for older browsers using execCommand
 * Creates a temporary textarea element to perform the copy operation
 *
 * @param {string} text - Text to copy
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback executed on successful copy
 * @param {Function} options.onError - Callback executed on copy failure
 * @returns {Promise<void>}
 */
function fallbackCopyToClipboard(text, options = {}) {
  const {
    onSuccess = () => showSuccessToast(FRIEND_PAY_MESSAGES.LINK_COPIED),
    onError = () => showErrorToast(FRIEND_PAY_MESSAGES.COPY_FAILED + text)
  } = options;

  return new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Position off-screen
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        onSuccess();
        resolve();
      } else {
        onError(new Error('execCommand failed'));
        reject(new Error('execCommand failed'));
      }
    } catch (err) {
      document.body.removeChild(textArea);
      onError(err);
      reject(err);
    }
  });
}

/**
 * Simple copy to clipboard without callbacks
 * Returns a promise that resolves/rejects based on success
 * Useful for programmatic copying without user notifications
 *
 * @param {string} text - Text to copy
 * @returns {Promise<void>}
 */
exports.copyToClipboardSilent = function(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }

  return new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        resolve();
      } else {
        reject(new Error('Copy failed'));
      }
    } catch (err) {
      document.body.removeChild(textArea);
      reject(err);
    }
  });
};
